import { useState } from 'react'
import { useCrashDetectionDashboardList } from '@/services/crash-detection/useCrashDetectionDashboardList'
import { useFilterNotificationStatus } from '@/services/crash-detection/useFilterNotificationStatus'
import { useFilterContactStatus } from '@/services/crash-detection/useFilterContactStatus'
import { CrashContactStatus, CrashUserRespondType } from '@/services/crash-detection/types'
import dayjs from 'dayjs'
import { ColumnsType } from 'antd/lib/table'
import { BlinkingWrapper, Dot } from './styles'
import { PhoneFilled } from '@ant-design/icons'
import { Space, TablePaginationConfig } from 'antd'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import { UserSelectStyle } from '@/components/common/Table/styles'
import { NotificationStatus, NOTIFICATION_STATUS_OPTIONS } from '@/constant/CRASH_DETECTION'
import { toThaiLocaleFullDateAndTime } from '@/libs/date'

const useCrashDetectionMonitorTableHook = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>({})
  const [formattedFilters, setFormattedFilters] = useState<string>('')
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)

  const { data, isLoading } = useCrashDetectionDashboardList({
    page: currentPage,
    limit: pageSize,
    search: '',
    filter: formattedFilters,
    refetchInterval: 15000, // Always 15 seconds auto-refresh for monitor page
  })

  const queryFilterNotificationStatus = useFilterNotificationStatus()
  const queryFilterContactStatus = useFilterContactStatus()

  const handleFilter = (newFilters: Record<string, FilterValue | null>) => {
    const filterAndPrefixList = [
      { key: 'notificationStatus', prefix: 'filter.notificationStatus' },
      { key: 'currentContactStatus', prefix: 'filter.currentContactStatus' },
      { key: 'currentIncidentStatus', prefix: 'filter.currentIncidentStatus' },
      { key: 'lastUpdateUserId', prefix: 'filter.lastUpdateUserId' },
    ]

    const formatted = Object.entries(newFilters)
      .map(([key, value]) => {
        const filterItem = filterAndPrefixList.find((item) => item.key === key)
        if (filterItem && value) {
          const prefix = filterItem.prefix
          if (Array.isArray(value)) {
            return value.map((v, i) => (i === 0 ? `${prefix}=$in:${v}` : String(v))).join(',')
          }
          return `${prefix}=$in:${value}`
        }
        return ''
      })
      .filter(Boolean)
      .join('&')

    setFormattedFilters(formatted)
  }

  const onTableChange = (
    pagination: TablePaginationConfig,
    newFilters: Record<string, FilterValue | null>,
    _sorter: SorterResult<{}> | SorterResult<{}>[],
  ) => {
    handleFilter(newFilters)
    setCurrentPage(pagination.current || 1)
    setPageSize(pagination.pageSize || 10)
    setFilters(newFilters)
  }

  const formatThaiBE = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-'
    return toThaiLocaleFullDateAndTime(String(dateStr)) || '-'
  }

  const getElapsed = (record: any) => {
    const isNotContacted =
      !record.currentContactStatus ||
      record.currentContactStatus === CrashContactStatus.NOT_CONTACTED
    const isCrash = record.userRespondType === CrashUserRespondType.CRASH
    if (!isCrash || !isNotContacted || !record.userRespondAt) return '-'
    const diff = dayjs().diff(dayjs(record.userRespondAt), 'minute')
    if (diff < 60) return `${diff} นาทีที่แล้ว`
    const hours = Math.floor(diff / 60)
    const mins = diff % 60
    return `${hours} ชม. ${mins > 0 ? `${mins} นาที` : '00 นาที'}`.trim()
  }

  const getNotificationStatus = (record: any) => {
    const userRespondType = record.userRespondType
    if (userRespondType === CrashUserRespondType.CRASH) {
      return NotificationStatus.CONFIRM_CRASH
    }
    if (userRespondType === CrashUserRespondType.NO_CRASH) {
      return NotificationStatus.NO_NEED_HELP
    }

    if (userRespondType === CrashUserRespondType.NO_ACTION) {
      return NotificationStatus.NO_ACTION
    }

    return NotificationStatus.NOTI_SENT
  }

  const columns: ColumnsType<any> = [
    {
      title: 'ลำดับ',
      key: 'order',
      width: 60,
      className: UserSelectStyle,
      render: (_: any, __: any, index: number) => {
        return (currentPage - 1) * pageSize + index + 1
      },
    },
    {
      title: 'สถานะการแจ้งเตือน',
      key: 'notificationStatus',
      width: 180,
      className: UserSelectStyle,
      filters:
        queryFilterNotificationStatus.data?.map((o) => ({
          text: o.name,
          value: o.code ?? 'null',
        })) || [],
      filteredValue: filters['notificationStatus'] || null,
      render: (_: any, record: any) => {
        const statusVal = getNotificationStatus(record)
        const opt = NOTIFICATION_STATUS_OPTIONS.find((o) => o.value === statusVal)
        if (!opt) return '-'

        const isNotContacted =
          !record.currentContactStatus ||
          record.currentContactStatus === CrashContactStatus.NOT_CONTACTED
        const showPhoneIcon = opt.hasPhoneIcon && isNotContacted

        return (
          <Space>
            {showPhoneIcon && (
              <BlinkingWrapper>
                <PhoneFilled style={{ fontSize: '12px', color: opt.color }} />
              </BlinkingWrapper>
            )}
            <Dot color={opt.color} />
            {opt.label}
          </Space>
        )
      },
    },
    {
      title: 'สถานะติดต่อ',
      dataIndex: 'currentContactStatus',
      key: 'currentContactStatus',
      width: 160,
      className: UserSelectStyle,
      filters:
        queryFilterContactStatus.data?.map((o) => ({
          text: o.name,
          value: o.code,
        })) || [],
      filteredValue: filters['currentContactStatus'] || null,
      render: (status: CrashContactStatus) => {
        const currentStatus = status || CrashContactStatus.NOT_CONTACTED
        // map API code to display label from filter data, fallback to static map
        const opt = queryFilterContactStatus.data?.find((o) => o.code === currentStatus)
        const colorMap: Record<string, string> = {
          [CrashContactStatus.CONTACTED]: '#52C41A',
          [CrashContactStatus.UNREACHABLE]: '#FF4D4F',
          [CrashContactStatus.NOT_CONTACTED]: '#FAAD14',
          [CrashContactStatus.NOT_APPLICABLE]: '#8C8C8C',
        }
        if (!opt) return '-'
        return (
          <Space>
            <Dot color={colorMap[currentStatus] || '#8C8C8C'} />
            {opt.name}
          </Space>
        )
      },
    },
    {
      title: 'วันและเวลาที่ลูกค้ากดยืนยันการเกิดเหตุ',
      dataIndex: 'userRespondAt',
      key: 'userRespondAt',
      width: 250,
      className: UserSelectStyle,
      render: (text: string) => formatThaiBE(text),
    },
    {
      title: 'เลขที่กรมธรรม์',
      dataIndex: 'policyNumber',
      key: 'policyNumber',
      width: 110,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ทะเบียนรถ',
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      width: 160,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ชื่อ-นามสกุล',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 180,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'เบอร์โทรศัพท์ติดต่อฉุกเฉิน',
      dataIndex: 'emergencyContactNo',
      key: 'emergencyContactNo',
      width: 160,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ระยะเวลาที่รอแผนกรับแจ้งติดต่อกลับ',
      key: 'elapsed',
      width: 220,
      className: UserSelectStyle,
      render: (_: any, record: any) => getElapsed(record),
    },
  ]

  return {
    data,
    isLoading,
    columns,
    onTableChange,
    setSelectedRowId,
    selectedRowId,
  }
}

export default useCrashDetectionMonitorTableHook
